import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Copy, Check, Trash2, Activity, AlertCircle, Plug, ShieldCheck, Sparkles, ShieldPlus, Stethoscope, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-zinc-950 p-5 bg-white" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className={`font-display text-3xl md:text-4xl font-black mt-2 tracking-tighter ${accent || "text-zinc-950"}`}>
        {value}
      </div>
    </div>
  );
}

export default function ScriptDeployment() {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [domain, setDomain] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [selected, setSelected] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [snippetLoading, setSnippetLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [flashJustCreated, setFlashJustCreated] = useState(false);
  const snippetPanelRef = useRef(null);

  // Validate-install diagnostic modal state
  const [validateOpen, setValidateOpen] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [validatingId, setValidatingId] = useState(null);

  const runValidate = async (dep) => {
    setValidateOpen(true);
    setValidateResult(null);
    setValidateLoading(true);
    setValidatingId(dep.id);
    try {
      const r = await api.post(`/v1/deployments/${dep.id}/validate-install`);
      setValidateResult(r.data);
      // Refresh list so the row's cached badge updates
      load(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
      setValidateOpen(false);
    } finally {
      setValidateLoading(false);
      setValidatingId(null);
    }
  };

  const load = async (preserveSelected = true) => {
    setLoading(true);
    try {
      const r = await api.get("/deployments");
      setDeployments(r.data);
      if (!preserveSelected || !selected) {
        if (r.data[0]) await selectDep(r.data[0]);
      }
    } catch {} finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, []);

  const selectDep = async (d) => {
    // Clear the stale snippet IMMEDIATELY so the UI never shows a domain/token mismatch.
    setSelected(d);
    setSnippet("");
    setSnippetLoading(true);
    try {
      const r = await api.get(`/deployments/${d.id}/snippet`);
      setSnippet(r.data.snippet);
    } catch {
      toast.error("Failed to fetch snippet");
    } finally {
      setSnippetLoading(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    const trimmedDomain = domain.trim();
    if (!trimmedDomain) return;
    setCreating(true);
    try {
      const r = await api.post(
        "/deployments",
        { client_domain: trimmedDomain, client_business_name: businessName || null },
      );
      const isNew = r.headers["x-resource-created"] !== "false" && r.status !== 200;
      if (isNew) {
        toast.success(`New snippet generated for ${r.data.client_domain}`);
      } else {
        toast.info(`Domain already deployed — reusing existing snippet for ${r.data.client_domain}`);
      }
      setDomain("");
      setBusinessName("");
      await load(true);
      await selectDep(r.data);

      // Visual cue: scroll the snippet panel into view and flash a highlight ring
      setFlashJustCreated(true);
      setTimeout(() => {
        snippetPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
      setTimeout(() => setFlashJustCreated(false), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this deployment? The script will deactivate.")) return;
    try {
      await api.delete(`/deployments/${id}`);
      toast.success("Removed");
      if (selected?.id === id) { setSelected(null); setSnippet(""); }
      await load(false);
    } catch { toast.error("Failed"); }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Clipboard blocked"); }
  };

  const activeCount = deployments.filter((d) => d.health_status === "ACTIVE").length;
  const totalPatches = deployments.reduce((s, d) => s + (d.background_patches_applied_count || 0), 0);

  return (
    <div data-testid="deployments-page">
      <PageHeader
        eyebrow="Module 04 · Silent Shield Script Manager"
        title="Active deployments"
        description="Generate a single line of async JavaScript per converted client. Shield runs silently — no widget, no logo wheel — and patches missing labels, contrast and keyboard controls on every page load."
        testid="deployments-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Total deployments" value={deployments.length} testid="stat-deployments-total" />
          <Stat label="Active" value={activeCount} accent="text-emerald-600" testid="stat-deployments-active" />
          <Stat label="Offline" value={deployments.length - activeCount} accent="text-red-600" testid="stat-deployments-offline" />
          <Stat label="Total patches applied" value={totalPatches.toLocaleString()} accent="text-blue-700" testid="stat-patches-total" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-6">
          {/* Deployments list */}
          <div className="border border-zinc-950">
            <div className="bg-zinc-950 text-white px-4 py-2 flex justify-between items-center">
              <div className="label-xs text-zinc-400">Client deployments</div>
              <div className="font-mono text-xs">{deployments.length}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-zinc-950">
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Status</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Domain</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Business</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Structural Repair</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Setup</th>
                    <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Patches</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin" /></td></tr>
                  )}
                  {!loading && deployments.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-zinc-500 text-sm">
                      <AlertCircle className="w-5 h-5 inline text-zinc-400 mr-2" />No deployments yet — create one below.
                    </td></tr>
                  )}
                  {deployments.map((d) => (
                    <tr
                      key={d.id}
                      onClick={() => selectDep(d)}
                      className={`border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer ${selected?.id === d.id ? "bg-zinc-100" : ""}`}
                      data-testid={`deployment-row-${d.id}`}
                    >
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          d.health_status === "ACTIVE" ? "bg-emerald-600 text-white" : "bg-red-100 text-red-800"
                        }`}>
                          <Activity className="w-3 h-3" /> {d.health_status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{d.client_domain}</td>
                      <td className="px-3 py-2 text-xs">{d.client_business_name || "—"}</td>
                      <td className="px-3 py-2">
                        {d.h1_remediated ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`h1-repair-badge-${d.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-600 text-emerald-900 text-[10px] font-bold uppercase tracking-wider font-mono cursor-help"
                                >
                                  <ShieldPlus className="w-3 h-3 text-emerald-700" />
                                  <span aria-hidden="true">🛡️</span> Shield Intercept Active: Missing H1 Repaired
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs bg-zinc-950 text-white text-xs leading-snug px-3 py-2 rounded-sm"
                                data-testid={`h1-repair-tooltip-${d.id}`}
                              >
                                The source server lacks a structural H1 tag, but the Silent Shield has dynamically patched the browser's accessibility tree to present a flawless document outline to screen readers and automated auditing legal bots.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-zinc-400 font-mono text-[10px]" data-testid={`h1-repair-none-${d.id}`}>—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{(d.setup_date || "").slice(0, 10)}</td>
                      <td className="px-3 py-2 font-mono text-xs font-bold">{d.background_patches_applied_count}</td>
                      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => runValidate(d)}
                            disabled={validatingId === d.id}
                            title="Validate Install — probe the live domain and diagnose snippet health"
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${
                              d.last_validation_overall === "PASS"
                                ? "border-emerald-600 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                                : d.last_validation_overall === "FAIL"
                                ? "border-red-600 text-red-800 bg-red-50 hover:bg-red-100"
                                : "border-zinc-700 text-zinc-800 bg-white hover:bg-zinc-100"
                            }`}
                            data-testid={`deployment-validate-${d.id}`}
                          >
                            {validatingId === d.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Stethoscope className="w-3 h-3" />
                            )}
                            Validate
                          </button>
                          <button
                            onClick={() => onDelete(d.id)}
                            className="text-zinc-400 hover:text-red-600"
                            data-testid={`deployment-delete-${d.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: create + snippet */}
          <div>
            <form onSubmit={onCreate} className="border border-zinc-950 p-5 bg-white mb-4" data-testid="deployment-create-form">
              <div className="label-xs mb-2">Create deployment</div>
              <Label className="text-xs font-bold">Client domain</Label>
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="acmebakery.com"
                className="rounded-sm h-11 mt-1 font-mono text-sm"
                required
                data-testid="deployment-domain-input"
              />
              <Label className="text-xs font-bold mt-3 block">Business name (optional)</Label>
              <Input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Bakery LLC"
                className="rounded-sm h-11 mt-1 text-sm"
                data-testid="deployment-business-input"
              />
              <Button
                type="submit"
                disabled={creating}
                className="w-full mt-4 h-11 rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider"
                data-testid="deployment-create-button"
              >
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Provisioning…</> : <><Plug className="w-4 h-4 mr-2" /> Generate Snippet</>}
              </Button>
            </form>

            {(selected || snippetLoading) && (
              <div
                ref={snippetPanelRef}
                data-testid="snippet-panel"
                className={`border-2 transition-all duration-500 ${
                  flashJustCreated ? "border-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.2)]" : "border-zinc-950"
                }`}
              >
                <div className="bg-zinc-950 text-white px-4 py-2 flex justify-between items-center">
                  <div className="label-xs text-zinc-400 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Silent Shield Snippet
                    {flashJustCreated && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500 text-zinc-950 text-[9px] font-black uppercase tracking-widest font-mono ml-1">
                        <Sparkles className="w-2.5 h-2.5" /> Just generated
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-400">async / non-blocking</div>
                </div>

                {/* Clear "current snippet for" header */}
                {selected && (
                  <div className="px-4 py-2 bg-zinc-100 border-b border-zinc-300 flex flex-wrap justify-between items-center gap-2" data-testid="snippet-for-header">
                    <div className="min-w-0">
                      <div className="label-xs text-zinc-500">Snippet for</div>
                      <div className="font-mono text-sm font-bold truncate text-zinc-950" data-testid="snippet-for-domain">
                        {selected.client_domain}
                      </div>
                      {selected.client_business_name && (
                        <div className="text-[11px] text-zinc-600 truncate">{selected.client_business_name}</div>
                      )}
                    </div>
                  </div>
                )}

                {snippetLoading ? (
                  <div className="bg-zinc-950 text-zinc-400 font-mono text-xs p-8 text-center" data-testid="snippet-loading">
                    <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Generating snippet…
                  </div>
                ) : (
                  <pre className="bg-zinc-950 text-emerald-400 font-mono text-xs p-4 overflow-x-auto whitespace-pre-wrap break-all" data-testid="snippet-code">
{snippet}
                  </pre>
                )}

                <div className="bg-zinc-900 px-4 py-3 flex flex-wrap gap-2 items-center justify-between">
                  <div className="text-[10px] font-mono text-zinc-400 break-all">
                    token: <span className="text-white" data-testid="snippet-token-value">{selected?.script_token || "—"}</span>
                  </div>
                  <Button
                    type="button"
                    onClick={copySnippet}
                    disabled={!snippet || snippetLoading}
                    className="rounded-sm bg-white text-zinc-950 hover:bg-zinc-200 font-bold uppercase tracking-wider"
                    data-testid="copy-snippet-button"
                  >
                    {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy</>}
                  </Button>
                </div>
                <div className="bg-zinc-50 p-4 border-t border-zinc-200">
                  <div className="label-xs mb-1">Install instructions for client</div>
                  <ol className="list-decimal pl-5 text-xs space-y-1 text-zinc-700">
                    <li>Paste the snippet just before the closing <span className="font-mono">&lt;/body&gt;</span> tag of every page.</li>
                    <li>No visible widget appears — the script silently patches accessibility on each page load.</li>
                    <li>Health pings are sent to this console; the monitor flips to <strong>ACTIVE</strong> within minutes.</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- Validate Install diagnostic modal ---------------- */}
      <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
        <DialogContent className="max-w-2xl rounded-none border-2 border-zinc-950" data-testid="validate-install-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <Stethoscope className="w-5 h-5" /> Validate Install
            </DialogTitle>
            <DialogDescription className="font-mono text-xs text-zinc-500">
              Probes the live client page, parses the snippet, checks token/host/HTTPS, and tests the CORS preflight against the beacon endpoint.
            </DialogDescription>
          </DialogHeader>

          {validateLoading && (
            <div className="py-10 text-center text-zinc-600" data-testid="validate-install-loading">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-3 text-blue-700" />
              <div className="text-sm font-mono">Probing live domain…</div>
              <div className="text-[11px] text-zinc-400 mt-1">7 checks · ~5-15 seconds</div>
            </div>
          )}

          {!validateLoading && validateResult && (
            <div data-testid="validate-install-result">
              <div
                className={`px-4 py-3 mb-3 flex items-center justify-between ${
                  validateResult.overall === "PASS"
                    ? "bg-emerald-600 text-white"
                    : validateResult.overall === "WARN"
                    ? "bg-amber-500 text-zinc-950"
                    : "bg-red-700 text-white"
                }`}
                data-testid={`validate-install-overall-${validateResult.overall}`}
              >
                <div className="flex items-center gap-2">
                  {validateResult.overall === "PASS" ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <div>
                    <div className="font-display text-lg font-black tracking-tight">
                      {validateResult.overall === "PASS"
                        ? "Install is healthy"
                        : validateResult.overall === "WARN"
                        ? "Install is degraded"
                        : "Install has problems"}
                    </div>
                    <div className="text-[11px] font-mono opacity-90">
                      {validateResult.client_domain} ·{" "}
                      {validateResult.fetched_url
                        ? validateResult.fetched_url.replace(/\?adacheck=\d+/, "")
                        : "unreachable"}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] font-mono opacity-80">
                  {new Date(validateResult.checked_at).toLocaleTimeString()}
                </div>
              </div>

              <ul className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {validateResult.checks.map((c) => (
                  <li
                    key={c.id}
                    className={`border-l-4 pl-3 py-2 ${
                      c.status === "pass"
                        ? "border-emerald-500 bg-emerald-50/50"
                        : c.status === "warn"
                        ? "border-amber-500 bg-amber-50/50"
                        : "border-red-600 bg-red-50/50"
                    }`}
                    data-testid={`validate-check-${c.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {c.status === "pass" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-700 flex-shrink-0" />
                      )}
                      <span className="font-display text-sm font-bold">{c.label}</span>
                    </div>
                    <div className="text-[12px] text-zinc-700 mt-1 break-all">{c.detail}</div>
                    {c.fix && c.status !== "pass" && (
                      <div className="mt-1.5 text-[11px] text-zinc-800 bg-white border border-zinc-300 px-2 py-1 font-mono">
                        <strong className="uppercase tracking-wider text-[10px] text-zinc-500">Fix:</strong>{" "}
                        {c.fix}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="border-t border-zinc-200 pt-3 mt-2">
            <Button
              type="button"
              onClick={() => {
                const dep = deployments.find((d) => d.id === validateResult?.deployment_id);
                if (dep) runValidate(dep);
              }}
              disabled={validateLoading || !validateResult}
              variant="outline"
              className="rounded-sm border-zinc-950 font-bold uppercase tracking-wider"
              data-testid="validate-install-rerun"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Re-run
            </Button>
            <Button
              type="button"
              onClick={() => setValidateOpen(false)}
              className="rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
              data-testid="validate-install-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
